import raw from '../../../../CHANGELOG.md?raw';

/**
 * Änderungsprotokoll (CHANGELOG.md aus dem Repo-Root, beim Build eingebettet).
 *
 * Bewusst kein Markdown-Paket: Das Format ist von uns vorgegeben
 * (docs/VERSIONIERUNG.md) und die CI erzwingt je Version einen Abschnitt —
 * ein kleiner Parser reicht und hält das Bundle schlank.
 */

export const changelogRaw: string = raw;

export interface ChangelogGroup {
  title: string; // z. B. "Behoben", "Neu", "Sicherheit"
  items: string[];
}

export interface ChangelogEntry {
  version: string; // z. B. "2026.8.6" oder "2026.8.0 – 2026.8.3"
  date: string; // z. B. "12.08.2026" (wie in der Datei, ohne Umdeutung)
  groups: ChangelogGroup[];
}

const HEADING = /^## (.+?)(?:\s+—\s+(.+))?$/;
const GROUP = /^\*\*(.+?)\*\*(.*)$/;

/** Entfernt einfache Markdown-Auszeichnung (fett) für die Anzeige. */
function plain(text: string): string {
  return text.replace(/\*\*(.+?)\*\*/g, '$1').trim();
}

export function parseChangelog(input: string = changelogRaw): ChangelogEntry[] {
  const entries: ChangelogEntry[] = [];
  let entry: ChangelogEntry | null = null;
  let group: ChangelogGroup | null = null;
  let pendingItem: string | null = null;

  const flushItem = () => {
    if (pendingItem !== null && group !== null) {
      group.items.push(plain(pendingItem));
    }
    pendingItem = null;
  };

  for (const line of input.split('\n')) {
    const heading = HEADING.exec(line);
    if (heading) {
      flushItem();
      entry = { version: heading[1].trim(), date: (heading[2] ?? '').trim(), groups: [] };
      group = null;
      entries.push(entry);
      continue;
    }
    if (!entry) continue; // Kopftext vor dem ersten Abschnitt

    const groupMatch = GROUP.exec(line.trim());
    if (groupMatch && !line.trim().startsWith('- ')) {
      flushItem();
      // Zusatz hinter der Gruppe (z. B. "— wirksam erst mit ...") mit anzeigen
      const suffix = plain(groupMatch[2] ?? '').replace(/^[\s—-]+/, '');
      group = { title: plain(groupMatch[1]) + (suffix ? ` — ${suffix}` : ''), items: [] };
      entry.groups.push(group);
      continue;
    }

    if (line.startsWith('- ')) {
      flushItem();
      if (!group) {
        group = { title: '', items: [] };
        entry.groups.push(group);
      }
      pendingItem = line.slice(2);
      continue;
    }

    // Fortsetzungszeile eines Listenpunkts (Einrückung)
    if (pendingItem !== null && line.startsWith('  ') && line.trim() !== '') {
      pendingItem += ` ${line.trim()}`;
    }
  }
  flushItem();

  return entries;
}

// -----------------------------------------------------------------------------
// "Neuigkeiten"-Punkt: zuletzt gesehene Version je Browser merken
// -----------------------------------------------------------------------------

export const CHANGELOG_STORAGE_KEY = 'washops.changelog.lastSeenVersion';
const STORAGE_KEY = CHANGELOG_STORAGE_KEY;

/** Neueste Version der Datei (erster Abschnitt). */
export function latestChangelogVersion(input: string = changelogRaw): string | null {
  return parseChangelog(input)[0]?.version ?? null;
}

export function hasUnseenChangelog(input: string = changelogRaw): boolean {
  const latest = latestChangelogVersion(input);
  if (!latest) return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== latest;
  } catch {
    return false; // Storage gesperrt (z. B. Kiosk-Modus): keinen Punkt zeigen
  }
}

export function markChangelogSeen(input: string = changelogRaw): void {
  const latest = latestChangelogVersion(input);
  if (!latest) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, latest);
  } catch {
    // Storage gesperrt: dann bleibt der Punkt eben stehen
  }
}
